/**
 * FieldSettingsPanel Component
 *
 * Modal dialog for comprehensive field configuration including all settings,
 * validation rules, conditional logic, and advanced options.
 */

import { memo, useCallback, useMemo, useState } from 'react';
import {
  Modal,
  Tabs,
  Stack,
  TextInput,
  Textarea,
  Select,
  NumberInput,
  Switch,
  Group,
  Button,
  ActionIcon,
  Text,
  Box,
  Paper,
  Divider,
} from '@mantine/core';
import { Icon } from '@/components/ui/icons/Icon';
import {
  useFormBuilderStore,
  selectSelectedField,
  selectShowFieldSettings,
  selectFields,
  selectMemberFieldOptions,
} from '@/stores/formBuilderStore';
import { usePreferencesStore } from '../../../stores';
import { LINE_HEIGHT_MAP, LETTER_SPACING_MAP } from '../../../hooks/useAccessibilityStyles';
import {
  FIELD_TYPE_CONFIGS,
  COLUMN_SPAN_OPTIONS,
  HEADING_LEVEL_OPTIONS,
} from './constants';
import {
  parseOptionsString,
  formatOptionsString,
  isValidFieldName,
} from './utils';
import type {
  FormFieldDefinition,
  ValidationRule,
  FieldCondition,
  ConditionalOperator,
  SelectOption,
} from './types';
import classes from './FormBuilder.module.css';

/**
 * FieldSettingsPanel Component
 */
const FieldSettingsPanel = memo(() => {
  const field = useFormBuilderStore(selectSelectedField);
  const showFieldSettings = useFormBuilderStore(selectShowFieldSettings);
  const fields = useFormBuilderStore(selectFields);
  const memberFieldOptions = useFormBuilderStore(selectMemberFieldOptions);
  const { setShowFieldSettings, updateField } = useFormBuilderStore();

  const lineSpacing = usePreferencesStore((state) => state.lineSpacing);
  const letterSpacingPref = usePreferencesStore((state) => state.letterSpacing);

  const accessibilityStyles = {
    lineHeight: LINE_HEIGHT_MAP[lineSpacing],
    letterSpacing: LETTER_SPACING_MAP[letterSpacingPref],
  };

  const handleClose = useCallback(() => {
    setShowFieldSettings(false);
  }, [setShowFieldSettings]);

  const handleUpdate = useCallback(
    (updates: Partial<FormFieldDefinition>) => {
      if (field) {
        updateField(field.id, updates);
      }
    },
    [field, updateField]
  );

  if (!field) return null;

  const config = FIELD_TYPE_CONFIGS[field.type];

  return (
    <Modal
      opened={showFieldSettings}
      onClose={handleClose}
      title={
        <Group gap="sm">
          <Icon name={config.icon} size={20} />
          <Text fw={600} style={accessibilityStyles}>Edit {config.label}</Text>
        </Group>
      }
      size="lg"
      padding="lg"
    >
      <Tabs defaultValue="general">
        <Tabs.List>
          <Tabs.Tab value="general" leftSection={<Icon name="settings" size={14} />}>
            General
          </Tabs.Tab>
          {!config.isLayoutElement && (
            <>
              <Tabs.Tab value="validation" leftSection={<Icon name="checkCircle" size={14} />}>
                Validation
              </Tabs.Tab>
              <Tabs.Tab value="conditional" leftSection={<Icon name="gitBranch" size={14} />}>
                Conditional
              </Tabs.Tab>
            </>
          )}
          {!config.isLayoutElement && (
            <Tabs.Tab value="ccms" leftSection={<Icon name="database" size={14} />}>
              Data &amp; Pricing
            </Tabs.Tab>
          )}
          <Tabs.Tab value="advanced" leftSection={<Icon name="code" size={14} />}>
            Advanced
          </Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="general" pt="md">
          <GeneralSettings field={field} onUpdate={handleUpdate} />
        </Tabs.Panel>

        {!config.isLayoutElement && (
          <>
            <Tabs.Panel value="validation" pt="md">
              <ValidationSettings field={field} onUpdate={handleUpdate} />
            </Tabs.Panel>

            <Tabs.Panel value="conditional" pt="md">
              <ConditionalSettings field={field} fields={fields} onUpdate={handleUpdate} />
            </Tabs.Panel>
          </>
        )}

        {!config.isLayoutElement && (
          <Tabs.Panel value="ccms" pt="md">
            <CcmsSettings field={field} memberFields={memberFieldOptions} onUpdate={handleUpdate} />
          </Tabs.Panel>
        )}

        <Tabs.Panel value="advanced" pt="md">
          <AdvancedSettings field={field} onUpdate={handleUpdate} />
        </Tabs.Panel>
      </Tabs>

      <Group justify="flex-end" mt="xl">
        <Button variant="subtle" onClick={handleClose}>
          Close
        </Button>
      </Group>
    </Modal>
  );
});

FieldSettingsPanel.displayName = 'FieldSettingsPanel';

/**
 * General Settings Tab
 */
const GeneralSettings = memo(
  ({
    field,
    onUpdate,
  }: {
    field: FormFieldDefinition;
    onUpdate: (updates: Partial<FormFieldDefinition>) => void;
  }) => {
    const config = FIELD_TYPE_CONFIGS[field.type];
    const [nameError, setNameError] = useState<string | null>(null);

    const handleNameChange = useCallback(
      (value: string) => {
        if (value && !isValidFieldName(value)) {
          setNameError('Start with a lower-case letter or _; use only letters, numbers, and underscores');
        } else {
          setNameError(null);
        }
        onUpdate({ name: value });
      },
      [onUpdate]
    );

    const optionsText = useMemo(() => {
      return formatOptionsString(field.options || []);
    }, [field.options]);

    const handleOptionsChange = useCallback(
      (text: string) => {
        // Keep per-option extras (price, capacity, description) for options that survive the edit.
        const options = parseOptionsString(text).map((opt) => {
          const prev = field.options?.find((p) => p.value === opt.value);
          return prev ? { ...prev, label: opt.label } : opt;
        });
        onUpdate({ options });
      },
      [onUpdate, field.options]
    );

    return (
      <Stack gap="md">
        <TextInput
          label="Label"
          value={field.label}
          onChange={(e) => onUpdate({ label: e.target.value })}
          placeholder="Field label"
          required
        />

        <TextInput
          label="Field Name"
          value={field.name}
          onChange={(e) => handleNameChange(e.target.value)}
          placeholder="field_name"
          description="Used as the key in form data. Use snake_case."
          error={nameError}
          required
        />

        {!config.isLayoutElement && (
          <>
            <TextInput
              label="Placeholder"
              value={field.placeholder || ''}
              onChange={(e) => onUpdate({ placeholder: e.target.value })}
              placeholder="Placeholder text"
            />

            <Textarea
              label="Description"
              value={field.description || ''}
              onChange={(e) => onUpdate({ description: e.target.value })}
              placeholder="Help text shown below the field"
              rows={2}
            />

            <TextInput
              label="Help Text"
              value={field.helpText || ''}
              onChange={(e) => onUpdate({ helpText: e.target.value })}
              placeholder="Additional guidance for users"
            />
          </>
        )}

        <Divider label="Layout" labelPosition="left" />

        <Select
          label="Column Span"
          value={String(field.colSpan || 1)}
          onChange={(value) => onUpdate({ colSpan: Number(value) as 1 | 2 | 3 | 4 })}
          data={COLUMN_SPAN_OPTIONS}
          description="How many grid columns this field spans"
        />

        {/* Type-specific settings */}
        {config.hasOptions && (
          <>
            <Divider label="Options" labelPosition="left" />
            <Textarea
              label="Options"
              value={optionsText}
              onChange={(e) => handleOptionsChange(e.target.value)}
              placeholder="value|Label&#10;option1|Option 1&#10;option2|Option 2"
              description="One option per line. Format: value|Label or just value"
              rows={6}
              autosize
              minRows={4}
              maxRows={12}
            />
          </>
        )}

        {config.hasMinMax && (
          <>
            <Divider label="Range" labelPosition="left" />
            <Group grow>
              <NumberInput
                label="Minimum"
                value={field.min}
                onChange={(value) => onUpdate({ min: value ? Number(value) : undefined })}
              />
              <NumberInput
                label="Maximum"
                value={field.max}
                onChange={(value) => onUpdate({ max: value ? Number(value) : undefined })}
              />
              {(field.type === 'slider' || field.type === 'number') && (
                <NumberInput
                  label="Step"
                  value={field.step || 1}
                  onChange={(value) => onUpdate({ step: Number(value) })}
                  min={0.01}
                />
              )}
            </Group>
          </>
        )}

        {field.type === 'heading' && (
          <>
            <Divider label="Heading" labelPosition="left" />
            <Select
              label="Heading Level"
              value={String(field.headingLevel || 2)}
              onChange={(value) => onUpdate({ headingLevel: Number(value) as 1 | 2 | 3 | 4 | 5 | 6 })}
              data={HEADING_LEVEL_OPTIONS}
            />
          </>
        )}

        {field.type === 'paragraph' && (
          <>
            <Divider label="Content" labelPosition="left" />
            <Textarea
              label="Paragraph Content"
              value={field.content || ''}
              onChange={(e) => onUpdate({ content: e.target.value })}
              placeholder="Enter the paragraph text..."
              rows={4}
              autosize
              minRows={3}
              maxRows={10}
            />
          </>
        )}

        {field.type === 'textarea' && (
          <>
            <Divider label="Textarea" labelPosition="left" />
            <NumberInput
              label="Number of Rows"
              value={field.rows || 4}
              onChange={(value) => onUpdate({ rows: Number(value) })}
              min={1}
              max={20}
            />
          </>
        )}

        {field.type === 'spacer' && (
          <>
            <Divider label="Spacer" labelPosition="left" />
            <NumberInput
              label="Height (pixels)"
              value={field.spacerHeight || 24}
              onChange={(value) => onUpdate({ spacerHeight: Number(value) })}
              min={8}
              max={200}
            />
          </>
        )}

        {(field.type === 'file' || field.type === 'image') && (
          <>
            <Divider label="File Upload" labelPosition="left" />
            <TextInput
              label="Accepted File Types"
              value={field.accept || ''}
              onChange={(e) => onUpdate({ accept: e.target.value })}
              placeholder={field.type === 'image' ? 'image/*' : '.pdf,.doc,.docx'}
              description="Comma-separated file extensions or MIME types"
            />
            <Switch
              label="Allow Multiple Files"
              checked={field.multiple || false}
              onChange={(e) => onUpdate({ multiple: e.currentTarget.checked })}
            />
          </>
        )}
      </Stack>
    );
  }
);

GeneralSettings.displayName = 'GeneralSettings';

/**
 * Validation Settings Tab
 */
const ValidationSettings = memo(
  ({
    field,
    onUpdate,
  }: {
    field: FormFieldDefinition;
    onUpdate: (updates: Partial<FormFieldDefinition>) => void;
  }) => {
    const [rules, setRules] = useState<ValidationRule[]>(field.validation || []);

    const addRule = useCallback(() => {
      const newRule: ValidationRule = {
        type: 'required',
        message: 'This field is required',
      };
      const updated = [...rules, newRule];
      setRules(updated);
      onUpdate({ validation: updated });
    }, [rules, onUpdate]);

    const updateRule = useCallback(
      (index: number, updates: Partial<ValidationRule>) => {
        const updated = rules.map((rule, i) =>
          i === index ? { ...rule, ...updates } : rule
        );
        setRules(updated);
        onUpdate({ validation: updated });
      },
      [rules, onUpdate]
    );

    const removeRule = useCallback(
      (index: number) => {
        const updated = rules.filter((_, i) => i !== index);
        setRules(updated);
        onUpdate({ validation: updated });
      },
      [rules, onUpdate]
    );

    return (
      <Stack gap="md">
        <Switch
          label="Required"
          description="User must fill in this field"
          checked={field.required || false}
          onChange={(e) => onUpdate({ required: e.currentTarget.checked })}
        />

        <Divider label="Validation Rules" labelPosition="left" />

        <Stack gap="sm">
          {rules.map((rule, index) => (
            <ValidationRuleEditor
              key={index}
              rule={rule}
              fieldType={field.type}
              onUpdate={(updates) => updateRule(index, updates)}
              onRemove={() => removeRule(index)}
            />
          ))}

          <Button
            variant="subtle"
            size="sm"
            leftSection={<Icon name="plus" size={14} />}
            onClick={addRule}
          >
            Add Validation Rule
          </Button>
        </Stack>
      </Stack>
    );
  }
);

ValidationSettings.displayName = 'ValidationSettings';

/**
 * Single validation rule editor
 */
const ValidationRuleEditor = memo(
  ({
    rule,
    fieldType,
    onUpdate,
    onRemove,
  }: {
    rule: ValidationRule;
    fieldType: string;
    onUpdate: (updates: Partial<ValidationRule>) => void;
    onRemove: () => void;
  }) => {
    const ruleTypes = [
      { value: 'required', label: 'Required' },
      { value: 'email', label: 'Email Format' },
      { value: 'url', label: 'URL Format' },
      { value: 'minLength', label: 'Minimum Length' },
      { value: 'maxLength', label: 'Maximum Length' },
      { value: 'min', label: 'Minimum Value' },
      { value: 'max', label: 'Maximum Value' },
      { value: 'pattern', label: 'Regex Pattern' },
    ];

    return (
      <Paper withBorder p="sm">
        <Group align="flex-start">
          <Stack gap="xs" style={{ flex: 1 }}>
            <Group grow>
              <Select
                size="sm"
                value={rule.type}
                onChange={(value) => onUpdate({ type: value as ValidationRule['type'] })}
                data={ruleTypes}
              />

              {(rule.type === 'minLength' || rule.type === 'maxLength' ||
                rule.type === 'min' || rule.type === 'max') && (
                <NumberInput
                  size="sm"
                  placeholder="Value"
                  value={rule.value as number}
                  onChange={(value) => onUpdate({ value: Number(value) })}
                />
              )}

              {rule.type === 'pattern' && (
                <TextInput
                  size="sm"
                  placeholder="Regex pattern"
                  value={(rule.value as string) || ''}
                  onChange={(e) => onUpdate({ value: e.target.value })}
                />
              )}
            </Group>

            <TextInput
              size="sm"
              placeholder="Error message"
              value={rule.message}
              onChange={(e) => onUpdate({ message: e.target.value })}
            />
          </Stack>

          <ActionIcon variant="subtle" color="red" onClick={onRemove}>
            <Icon name="trash" size={14} />
          </ActionIcon>
        </Group>
      </Paper>
    );
  }
);

ValidationRuleEditor.displayName = 'ValidationRuleEditor';

/**
 * Conditional Settings Tab
 */
const ConditionalSettings = memo(
  ({
    field,
    fields,
    onUpdate,
  }: {
    field: FormFieldDefinition;
    fields: FormFieldDefinition[];
    onUpdate: (updates: Partial<FormFieldDefinition>) => void;
  }) => {
    const lineSpacing = usePreferencesStore((state) => state.lineSpacing);
    const letterSpacingPref = usePreferencesStore((state) => state.letterSpacing);

    const accessibilityStyles = {
      lineHeight: LINE_HEIGHT_MAP[lineSpacing],
      letterSpacing: LETTER_SPACING_MAP[letterSpacingPref],
    };

    const [conditions, setConditions] = useState<FieldCondition[]>(field.showWhen || []);

    const otherFields = useMemo(
      () => fields.filter((f) => f.id !== field.id && !FIELD_TYPE_CONFIGS[f.type].isLayoutElement),
      [fields, field.id]
    );

    const addCondition = useCallback(() => {
      if (otherFields.length === 0) return;

      const newCondition: FieldCondition = {
        field: otherFields[0].name,
        operator: 'eq',
        value: '',
      };
      const updated = [...conditions, newCondition];
      setConditions(updated);
      onUpdate({ showWhen: updated });
    }, [conditions, otherFields, onUpdate]);

    const updateCondition = useCallback(
      (index: number, updates: Partial<FieldCondition>) => {
        const updated = conditions.map((cond, i) =>
          i === index ? { ...cond, ...updates } : cond
        );
        setConditions(updated);
        onUpdate({ showWhen: updated });
      },
      [conditions, onUpdate]
    );

    const removeCondition = useCallback(
      (index: number) => {
        const updated = conditions.filter((_, i) => i !== index);
        setConditions(updated);
        onUpdate({ showWhen: updated.length > 0 ? updated : undefined });
      },
      [conditions, onUpdate]
    );

    if (otherFields.length === 0) {
      return (
        <Text c="dimmed" ta="center" py="xl" style={accessibilityStyles}>
          Add more fields to the form to set up conditional visibility.
        </Text>
      );
    }

    return (
      <Stack gap="md">
        <Text size="sm" c="dimmed" style={accessibilityStyles}>
          Show this field only when the following conditions are met:
        </Text>

        {conditions.length > 0 && (
          <Select
            label="Match"
            value={field.showWhenLogic || 'and'}
            onChange={(value) => onUpdate({ showWhenLogic: value as 'and' | 'or' })}
            data={[
              { value: 'and', label: 'All conditions (AND)' },
              { value: 'or', label: 'Any condition (OR)' },
            ]}
            size="sm"
            w={200}
          />
        )}

        <Stack gap="sm">
          {conditions.map((condition, index) => (
            <ConditionEditor
              key={index}
              condition={condition}
              fields={otherFields}
              onUpdate={(updates) => updateCondition(index, updates)}
              onRemove={() => removeCondition(index)}
            />
          ))}

          <Button
            variant="subtle"
            size="sm"
            leftSection={<Icon name="plus" size={14} />}
            onClick={addCondition}
          >
            Add Condition
          </Button>
        </Stack>
      </Stack>
    );
  }
);

ConditionalSettings.displayName = 'ConditionalSettings';

/**
 * Single condition editor
 */
const ConditionEditor = memo(
  ({
    condition,
    fields,
    onUpdate,
    onRemove,
  }: {
    condition: FieldCondition;
    fields: FormFieldDefinition[];
    onUpdate: (updates: Partial<FieldCondition>) => void;
    onRemove: () => void;
  }) => {
    const operators: { value: ConditionalOperator; label: string }[] = [
      { value: 'eq', label: 'Equals' },
      { value: 'neq', label: 'Not Equals' },
      { value: 'gt', label: 'Greater Than' },
      { value: 'lt', label: 'Less Than' },
      { value: 'gte', label: 'Greater or Equal' },
      { value: 'lte', label: 'Less or Equal' },
      { value: 'contains', label: 'Contains' },
      { value: 'notContains', label: 'Not Contains' },
      { value: 'isEmpty', label: 'Is Empty' },
      { value: 'isNotEmpty', label: 'Is Not Empty' },
    ];

    const needsValue = !['isEmpty', 'isNotEmpty'].includes(condition.operator);
    const selectedField = fields.find((f) => f.name === condition.field);

    return (
      <Paper withBorder p="sm">
        <Group align="flex-end">
          <Select
            label="Field"
            size="sm"
            value={condition.field}
            onChange={(value) => onUpdate({ field: value || '' })}
            data={fields.map((f) => ({ value: f.name, label: f.label }))}
            style={{ flex: 1 }}
          />

          <Select
            label="Operator"
            size="sm"
            value={condition.operator}
            onChange={(value) => onUpdate({ operator: value as ConditionalOperator })}
            data={operators}
            style={{ flex: 1 }}
          />

          {needsValue && (
            selectedField?.options ? (
              <Select
                label="Value"
                size="sm"
                value={String(condition.value || '')}
                onChange={(value) => onUpdate({ value })}
                data={selectedField.options}
                style={{ flex: 1 }}
              />
            ) : (
              <TextInput
                label="Value"
                size="sm"
                value={String(condition.value || '')}
                onChange={(e) => onUpdate({ value: e.target.value })}
                style={{ flex: 1 }}
              />
            )
          )}

          <ActionIcon variant="subtle" color="red" onClick={onRemove} mb={4}>
            <Icon name="trash" size={14} />
          </ActionIcon>
        </Group>
      </Paper>
    );
  }
);

ConditionEditor.displayName = 'ConditionEditor';

/**
 * Advanced Settings Tab
 */
const AdvancedSettings = memo(
  ({
    field,
    onUpdate,
  }: {
    field: FormFieldDefinition;
    onUpdate: (updates: Partial<FormFieldDefinition>) => void;
  }) => {
    const config = FIELD_TYPE_CONFIGS[field.type];

    return (
      <Stack gap="md">
        <TextInput
          label="CSS Class Name"
          value={field.className || ''}
          onChange={(e) => onUpdate({ className: e.target.value })}
          placeholder="custom-class"
          description="Additional CSS class for custom styling"
        />

        {!config.isLayoutElement && (
          <>
            <Divider label="State" labelPosition="left" />

            <Switch
              label="Disabled"
              description="Field is visible but cannot be edited"
              checked={field.disabled || false}
              onChange={(e) => onUpdate({ disabled: e.currentTarget.checked })}
            />

            <Switch
              label="Read Only"
              description="Field value can be selected but not changed"
              checked={field.readOnly || false}
              onChange={(e) => onUpdate({ readOnly: e.currentTarget.checked })}
            />
          </>
        )}

        <Divider label="Localization" labelPosition="left" />

        <TextInput
          label="Label Key"
          value={field.labelKey || ''}
          onChange={(e) => onUpdate({ labelKey: e.target.value })}
          placeholder="forms.myField.label"
          description="i18n key for the label"
        />

        {!config.isLayoutElement && (
          <>
            <TextInput
              label="Placeholder Key"
              value={field.placeholderKey || ''}
              onChange={(e) => onUpdate({ placeholderKey: e.target.value })}
              placeholder="forms.myField.placeholder"
              description="i18n key for the placeholder"
            />

            <TextInput
              label="Description Key"
              value={field.descriptionKey || ''}
              onChange={(e) => onUpdate({ descriptionKey: e.target.value })}
              placeholder="forms.myField.description"
              description="i18n key for the description"
            />
          </>
        )}
      </Stack>
    );
  }
);

AdvancedSettings.displayName = 'AdvancedSettings';

// ============================================================================
// CCMS settings (Forms Management extensions)
// ============================================================================

/** Types a participant sub-field may be (depth 1 — never nested participants). */
const PARTICIPANT_SUBFIELD_TYPES: { value: FormFieldDefinition['type']; label: string }[] = [
  { value: 'text', label: 'Text' },
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Phone' },
  { value: 'date', label: 'Date' },
  { value: 'number', label: 'Number' },
  { value: 'select', label: 'Dropdown' },
  { value: 'textarea', label: 'Long text' },
  { value: 'checkbox', label: 'Checkbox' },
];

const memberFieldLabel = (f: string) => f.replace(/^Member\./, '').replace(/([a-z])([A-Z])/g, '$1 $2');

/**
 * Member mapping, sensitivity, reporting, pricing/capacity and the CCMS-specific
 * per-type settings (participants, consent, quantity, pattern, upload size).
 */
const CcmsSettings = memo(
  ({
    field,
    memberFields,
    onUpdate,
  }: {
    field: FormFieldDefinition;
    memberFields: string[];
    onUpdate: (updates: Partial<FormFieldDefinition>) => void;
  }) => {
    const config = FIELD_TYPE_CONFIGS[field.type];
    const memberFieldData = useMemo(
      () => memberFields.map((f) => ({ value: f, label: memberFieldLabel(f) })),
      [memberFields]
    );

    const updateOption = useCallback(
      (index: number, updates: Partial<SelectOption>) => {
        const options = (field.options || []).map((o, i) => (i === index ? { ...o, ...updates } : o));
        onUpdate({ options });
      },
      [field.options, onUpdate]
    );

    return (
      <Stack gap="md">
        {memberFieldData.length > 0 && field.type !== 'participants' && (
          <Select
            label="Maps to member field"
            description="Used to prefill for signed-in members and when converting an entry into a member"
            placeholder="Not mapped"
            data={memberFieldData}
            value={field.memberField || null}
            onChange={(value) => onUpdate({ memberField: value || undefined })}
            clearable
            searchable
          />
        )}

        <Switch
          label="Sensitive"
          description="Medical, allergy or ID data — masked for users without the View Sensitive right"
          checked={field.sensitive || false}
          onChange={(e) => onUpdate({ sensitive: e.currentTarget.checked })}
        />
        <Switch
          label="Include in exports & reports"
          checked={field.reportable !== false}
          onChange={(e) => onUpdate({ reportable: e.currentTarget.checked })}
        />

        {(field.type === 'text' || field.type === 'phone') && (
          <TextInput
            label="Pattern (regex)"
            description="Optional — the value must match, e.g. ^[0-9]{13}$ for an SA ID number"
            placeholder="^[0-9]{13}$"
            value={field.pattern || ''}
            onChange={(e) => onUpdate({ pattern: e.target.value || undefined })}
          />
        )}

        {(field.type === 'file' || field.type === 'image') && (
          <NumberInput
            label="Max file size (MB)"
            value={field.maxSizeMb ?? ''}
            onChange={(value) => onUpdate({ maxSizeMb: value === '' ? undefined : Number(value) })}
            min={1}
            max={100}
          />
        )}

        {field.type === 'quantity' && (
          <NumberInput
            label="Unit price"
            description="Charged per unit when payment is enabled on the form"
            value={field.unitPrice ?? ''}
            onChange={(value) => onUpdate({ unitPrice: value === '' ? undefined : Number(value) })}
            min={0}
            decimalScale={2}
          />
        )}

        {config.hasOptions && (field.options?.length ?? 0) > 0 && (
          <>
            <Divider label="Option price & capacity" labelPosition="left" />
            <Text size="xs" c="dimmed">
              A price is added to the entry total when the option is chosen. A capacity limits how
              many entries may choose it (a capacity bucket is created on publish).
            </Text>
            <Stack gap={6}>
              {(field.options || []).map((opt, index) => (
                <Group key={`${opt.value}-${index}`} gap="xs" wrap="nowrap" align="flex-end">
                  <Text size="sm" style={{ flex: 1 }} lineClamp={1}>
                    {opt.label}
                  </Text>
                  <NumberInput
                    size="xs"
                    w={110}
                    placeholder="Price"
                    value={opt.price ?? ''}
                    onChange={(value) => updateOption(index, { price: value === '' ? undefined : Number(value) })}
                    min={0}
                    decimalScale={2}
                  />
                  <NumberInput
                    size="xs"
                    w={110}
                    placeholder="Capacity"
                    value={opt.capacity ?? ''}
                    onChange={(value) => updateOption(index, { capacity: value === '' ? undefined : Number(value) })}
                    min={0}
                  />
                </Group>
              ))}
            </Stack>
          </>
        )}

        {field.type === 'consent' && (
          <>
            <Divider label="Consent" labelPosition="left" />
            <Textarea
              label="Consent text"
              description="Shown next to the checkbox; the accepted text version is recorded with the entry"
              value={field.consentText || ''}
              onChange={(e) => onUpdate({ consentText: e.target.value })}
              autosize
              minRows={3}
              maxRows={8}
            />
            <TextInput
              label="Consent version"
              description="Bump this whenever the text changes"
              value={field.consentVersion || ''}
              onChange={(e) => onUpdate({ consentVersion: e.target.value })}
            />
          </>
        )}

        {field.type === 'participants' && (
          <ParticipantSettings field={field} memberFields={memberFieldData} onUpdate={onUpdate} />
        )}
      </Stack>
    );
  }
);

CcmsSettings.displayName = 'CcmsSettings';

const ParticipantSettings = memo(
  ({
    field,
    memberFields,
    onUpdate,
  }: {
    field: FormFieldDefinition;
    memberFields: { value: string; label: string }[];
    onUpdate: (updates: Partial<FormFieldDefinition>) => void;
  }) => {
    const subFields = field.participantFields || [];
    const relationshipText = useMemo(
      () => formatOptionsString(field.relationshipOptions || []),
      [field.relationshipOptions]
    );

    const updateSub = (index: number, updates: Partial<FormFieldDefinition>) => {
      onUpdate({
        participantFields: subFields.map((f, i) => (i === index ? { ...f, ...updates } : f)),
      });
    };

    const addSub = () => {
      const id = `${field.id}_${Date.now().toString(36)}`;
      onUpdate({
        participantFields: [
          ...subFields,
          { id, type: 'text', name: `field_${subFields.length + 1}`, label: 'New field', required: false },
        ],
      });
    };

    const removeSub = (index: number) => {
      onUpdate({ participantFields: subFields.filter((_, i) => i !== index) });
    };

    return (
      <>
        <Divider label="People" labelPosition="left" />
        <Group grow>
          <NumberInput
            label="Minimum people"
            value={field.minItems ?? ''}
            onChange={(value) => onUpdate({ minItems: value === '' ? undefined : Number(value) })}
            min={0}
          />
          <NumberInput
            label="Maximum people"
            value={field.maxItems ?? ''}
            onChange={(value) => onUpdate({ maxItems: value === '' ? undefined : Number(value) })}
            min={1}
          />
        </Group>
        <Textarea
          label="Relationship options"
          description="One per line (value|Label or just value). Leave empty to hide the relationship picker."
          value={relationshipText}
          onChange={(e) => onUpdate({ relationshipOptions: parseOptionsString(e.target.value) })}
          autosize
          minRows={2}
          maxRows={6}
        />

        <Divider label="Details per person" labelPosition="left" />
        <Stack gap="xs">
          {subFields.map((sub, index) => (
            <Paper key={sub.id || index} withBorder p="sm">
              <Stack gap={6}>
                <Group gap="xs" grow>
                  <TextInput
                    size="xs"
                    label="Label"
                    value={sub.label}
                    onChange={(e) => updateSub(index, { label: e.target.value })}
                  />
                  <TextInput
                    size="xs"
                    label="Key"
                    value={sub.name}
                    error={sub.name && !isValidFieldName(sub.name) ? 'Start lower-case; letters, numbers, _' : undefined}
                    onChange={(e) => updateSub(index, { name: e.target.value })}
                  />
                  <Select
                    size="xs"
                    label="Type"
                    data={PARTICIPANT_SUBFIELD_TYPES}
                    value={sub.type}
                    onChange={(value) => value && updateSub(index, { type: value as FormFieldDefinition['type'] })}
                  />
                </Group>
                {sub.type === 'select' && (
                  <Textarea
                    size="xs"
                    label="Options"
                    description="One per line: value|Label"
                    value={formatOptionsString(sub.options || [])}
                    onChange={(e) => updateSub(index, { options: parseOptionsString(e.target.value) })}
                    autosize
                    minRows={2}
                  />
                )}
                <Group gap="md" align="flex-end">
                  {memberFields.length > 0 && (
                    <Select
                      size="xs"
                      label="Maps to"
                      placeholder="Not mapped"
                      data={memberFields}
                      value={sub.memberField || null}
                      onChange={(value) => updateSub(index, { memberField: value || undefined })}
                      clearable
                      style={{ flex: 1 }}
                    />
                  )}
                  <Switch
                    size="xs"
                    label="Required"
                    checked={sub.required || false}
                    onChange={(e) => updateSub(index, { required: e.currentTarget.checked })}
                  />
                  <Switch
                    size="xs"
                    label="Sensitive"
                    checked={sub.sensitive || false}
                    onChange={(e) => updateSub(index, { sensitive: e.currentTarget.checked })}
                  />
                  <ActionIcon variant="subtle" color="red" onClick={() => removeSub(index)} aria-label="Remove field">
                    <Icon name="trash" size={14} />
                  </ActionIcon>
                </Group>
              </Stack>
            </Paper>
          ))}
          <Button variant="subtle" size="sm" leftSection={<Icon name="plus" size={14} />} onClick={addSub}>
            Add person field
          </Button>
        </Stack>
      </>
    );
  }
);

ParticipantSettings.displayName = 'ParticipantSettings';

export default FieldSettingsPanel;
